import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    Upload,
    Receipt,
    Package,
    Store,
    Bell,
    Users,
    Wallet,
    FileSpreadsheet,
    Settings as SettingsIcon,
    ScanLine,
    ScanText,
    Milk,
    ShieldCheck,
    FileText,
    BookOpen,
    LogOut
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '../lib/utils';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Logo } from '../brand';

const NAV = [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard', end: true },
    { to: '/upload', label: 'Upload', icon: Upload, testid: 'nav-upload' },
    { to: '/bills', label: 'Bills', icon: Receipt, testid: 'nav-bills' },
    { to: '/library', label: 'Library', icon: BookOpen, testid: 'nav-library', badge: 'New' },
    { to: '/items', label: 'Items', icon: Package, testid: 'nav-items' },
    { to: '/vendors', label: 'Vendors', icon: Store, testid: 'nav-vendors' },
    { to: '/staff', label: 'Staff', icon: Users, testid: 'nav-staff' },
    { to: '/expenses', label: 'Expenses', icon: Wallet, testid: 'nav-expenses' },
    { to: '/vouchers', label: 'Vouchers', icon: Receipt, testid: 'nav-vouchers', badge: 'New' },
    { to: '/invoicing', label: 'Invoicing', icon: FileText, testid: 'nav-invoicing', badge: 'New' },
    { to: '/alerts', label: 'Alerts', icon: Bell, testid: 'nav-alerts' },
    { to: '/ca-export', label: 'Export', icon: FileSpreadsheet, testid: 'nav-ca-export' },
    { to: '/settings', label: 'Settings', icon: SettingsIcon, testid: 'nav-settings' }
];

export function Sidebar({ alertsCount = 0, scannerEnabled = false }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();

    // Sprint 2026-01 / 2026-07: probe the two allowlist endpoints:
    //   • /api/scanner/whoami  → gates Daily Sheet + Vijay Ras entries.
    //     (Larger allowlist: OWNER_EMAIL + OWNER_SCANNER_EMAILS.)
    //   • /api/owner/whoami    → gates the Owner super-admin dashboard.
    //     (Single OWNER_EMAIL — cross-tenant view, do NOT open up.)
    // 403s are silent; the entry simply isn't rendered.
    //
    // On top of the whoami probe, Daily Sheet + Vijay Ras ALSO require the
    // user to have explicitly opted in via Settings → "Scanner Tools"
    // (scanner_enabled flag). This keeps the nav lean by default even for
    // allowlisted users.
    const [isOwner, setIsOwner] = useState(false);
    const [isScannerUser, setIsScannerUser] = useState(false);
    // Sprint 2026-07 (B): poll the review-queue count so the Bills nav can
    // show a "you have N bills waiting" badge. Every 30s is enough — this
    // is a passive breadcrumb, not real-time.
    const [reviewCount, setReviewCount] = useState(0);
    useEffect(() => {
        let cancelled = false;
        api.ownerWhoami()
            .then(() => { if (!cancelled) setIsOwner(true); })
            .catch(() => { if (!cancelled) setIsOwner(false); });
        api.scannerWhoami()
            .then(() => { if (!cancelled) setIsScannerUser(true); })
            .catch(() => { if (!cancelled) setIsScannerUser(false); });
        const fetchReview = () => {
            api.billsReviewCount()
                .then((r) => { if (!cancelled) setReviewCount(r?.count || 0); })
                .catch(() => { /* silent — nav badge is best-effort */ });
        };
        fetchReview();
        const t = setInterval(fetchReview, 30000);
        return () => { cancelled = true; clearInterval(t); };
    }, [user?.email]);

    // Sprint C: when alerts are toggled off, App.js passes alertsCount === null
    // so we hide the nav entry entirely.
    const alertsHidden = alertsCount === null;
    let visibleNav = alertsHidden ? NAV.filter((n) => n.to !== '/alerts') : NAV;
    if (isScannerUser && scannerEnabled) {
        visibleNav = [
            ...visibleNav,
            { to: '/daily-sheet', label: 'Daily Sheet', icon: ScanText, testid: 'nav-daily-sheet' },
            { to: '/vijay-ras', label: 'Vijay Ras', icon: Milk, testid: 'nav-vijay-ras' },
        ];
    }
    if (isOwner) {
        visibleNav = [
            ...visibleNav,
            { to: '/owner', label: 'Owner', icon: ShieldCheck, testid: 'nav-owner' },
        ];
    }
    return (
        <aside
            data-testid="sidebar"
            className="hidden md:flex md:flex-col md:w-[260px] shrink-0 border-r border-border/80 bg-card/60 backdrop-blur"
        >
            <div className="flex items-center gap-2.5 px-5 py-4">
                <Logo variant="icon" size={40} data-testid="brand-logo-sidebar" />
                <div className="min-w-0">
                    <div className="font-heading text-lg leading-tight">Ease My Bill</div>
                    <div className="text-[11px] text-muted-foreground">Snap. Scan. Save.</div>
                </div>
            </div>
            <nav className="px-3 py-2 flex-1 space-y-1">
                {visibleNav.map((n) => (
                    <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.end}
                        data-testid={n.testid}
                        className={({ isActive }) =>
                            cn(
                                'group flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors',
                                isActive && 'text-foreground bg-muted/80 border-l-2 border-primary'
                            )
                        }
                    >
                        <n.icon size={18} strokeWidth={1.75} />
                        <span className="flex-1">{n.label}</span>
                        {n.badge && (
                            <span className="inline-flex items-center justify-center px-1.5 py-0 rounded text-[9px] font-semibold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                                {n.badge}
                            </span>
                        )}
                        {n.to === '/alerts' && alertsCount > 0 && (
                            <span
                                data-testid="sidebar-alerts-badge"
                                className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-xs bg-[hsl(var(--price-increase-bg))] text-[hsl(var(--price-increase-fg))] border border-[hsl(var(--price-increase-border))] tabular-nums font-mono"
                            >
                                {alertsCount}
                            </span>
                        )}
                        {n.to === '/bills' && reviewCount > 0 && (
                            <span
                                data-testid="sidebar-review-badge"
                                title={`${reviewCount} bill${reviewCount === 1 ? '' : 's'} need${reviewCount === 1 ? 's' : ''} your review`}
                                className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-xs bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-800 tabular-nums font-mono"
                            >
                                {reviewCount}
                            </span>
                        )}
                    </NavLink>
                ))}
            </nav>
            <div className="px-4 py-4 border-t border-border/70 flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-medium">
                    {(user?.email || 'U').slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-sm truncate" data-testid="sidebar-user-email">{user?.email}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Owner</div>
                </div>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                        logout();
                        nav('/login', { replace: true });
                    }}
                    aria-label="Log out"
                    data-testid="sidebar-logout"
                >
                    <LogOut size={16} />
                </Button>
            </div>
        </aside>
    );
}
